/**
 * Apply scene.json `effects` blocks at runtime.
 *
 * Two kinds:
 *   - SPLAT reveal effects (radialWave) → the engine's official
 *     GSplatRevealRadial shader-effect script (renamed from GsplatRevealRadial
 *     in engine 2.21), attached to the splat entity.
 *     It auto-advances its own clock, so the reveal plays once on load —
 *     exactly what a demo intro wants. Editor-custom splat effects with no
 *     engine equivalent (particleReveal / fade / dissolve / growScale /
 *     revealRadius) are skipped with a console note (stock viewer shader
 *     has no uniforms for them — would need a custom chunk; out of scope
 *     for the first demos, radialWave is the headline).
 *   - MESH material effects (opacity / diffuse / emissive / metalness /
 *     gloss / bumpiness / specularity / visible) → a small keyframe driver
 *     that lerps the values over the authored frame span (once, at 30fps)
 *     and writes them to the StandardMaterial with update(). Mirrors the
 *     editor's model-effect adapters.
 *
 * Returns a disposer that stops the mesh ticker. Splat scripts are torn
 * down with their entities when the scene root is destroyed.
 */

import * as pc from 'playcanvas';
import { GSplatRevealRadial } from 'playcanvas/scripts/esm/gsplat/reveal-radial.mjs';

import type {
    Keyframe, MeshEffects, SplatEffects
} from './scene-types';

const FPS = 30;   // assumed timeline authoring rate (scene.json carries frames, not seconds)

/** Linear-interp a [[frame, value], ...] channel at frame f. Holds ends. */
function sampleScalar(keys: Keyframe[] | undefined, f: number, dflt: number): number {
    if (!keys || keys.length === 0) return dflt;
    if (f <= keys[0][0]) return keys[0][1];
    if (f >= keys[keys.length - 1][0]) return keys[keys.length - 1][1];
    for (let i = 0; i < keys.length - 1; i++) {
        const [f0, v0] = keys[i];
        const [f1, v1] = keys[i + 1];
        if (f >= f0 && f <= f1) {
            const t = (f - f0) / Math.max(1e-6, f1 - f0);
            return v0 + (v1 - v0) * t;
        }
    }
    return keys[keys.length - 1][1];
}

/** Sample a [[frame, r, g, b], ...] color channel at frame f into out. */
function sampleColor(keys: Keyframe[] | undefined, f: number, out: pc.Color): pc.Color | null {
    if (!keys || keys.length === 0) return null;
    const clampKey = (k: Keyframe) => out.set(k[1], k[2], k[3], 1);
    if (f <= keys[0][0]) return clampKey(keys[0]);
    if (f >= keys[keys.length - 1][0]) return clampKey(keys[keys.length - 1]);
    for (let i = 0; i < keys.length - 1; i++) {
        const a = keys[i];
        const b = keys[i + 1];
        if (f >= a[0] && f <= b[0]) {
            const t = (f - a[0]) / Math.max(1e-6, b[0] - a[0]);
            return out.set(
                a[1] + (b[1] - a[1]) * t,
                a[2] + (b[2] - a[2]) * t,
                a[3] + (b[3] - a[3]) * t,
                1
            );
        }
    }
    return clampKey(keys[keys.length - 1]);
}

function lastFrame(...channels: (Keyframe[] | undefined)[]): number {
    let max = 0;
    for (const ch of channels) {
        if (ch && ch.length) max = Math.max(max, ch[ch.length - 1][0]);
    }
    return max;
}

interface MeshTarget {
    material: pc.StandardMaterial;
    entity: pc.Entity;
    fx: MeshEffects;
    span: number;   // last frame across all channels
}

export function applyEffects(
    app: pc.AppBase,
    splats: Map<string, pc.Entity>,
    models: Map<string, pc.Entity>,
    splatFx: Map<string, SplatEffects>,
    meshFx: Map<string, MeshEffects>
): () => void {
    // ── Splat reveal scripts (engine GSplatRevealRadial) ──────────────────
    for (const [id, entity] of splats) {
        const fx = splatFx.get(id);
        if (!fx) continue;

        if (fx.radialWave) {
            const r = fx.radialWave;
            try {
                if (!entity.script) entity.addComponent('script');
                const attrs: Record<string, unknown> = {};
                if (r.center) attrs.center = new pc.Vec3(r.center[0], r.center[1], r.center[2]);
                if (typeof r.speed === 'number') attrs.speed = r.speed;
                if (typeof r.delay === 'number') attrs.delay = r.delay;
                if (typeof r.endRadius === 'number') attrs.endRadius = r.endRadius;
                if (typeof r.bandWidth === 'number') attrs.bandWidth = r.bandWidth;
                if (typeof r.oscillation === 'number') attrs.oscillationIntensity = r.oscillation;
                if (r.dotTint) attrs.dotTint = new pc.Color(r.dotTint[0], r.dotTint[1], r.dotTint[2]);
                if (r.liftTint) attrs.waveTint = new pc.Color(r.liftTint[0], r.liftTint[1], r.liftTint[2]);
                // @ts-expect-error — engine ESM script create() typing is loose
                entity.script.create(GSplatRevealRadial, { attributes: attrs });
            } catch (err) {
                console.warn(`[viewer2] radialWave reveal failed on splat "${id}" — splat still renders`, err);
            }
        }

        const unsupported = ['particleReveal', 'fade', 'dissolve', 'growScale', 'revealRadius']
            .filter(k => (fx as Record<string, unknown>)[k]);
        if (unsupported.length) {
            console.info(`[viewer2] splat "${id}" effects ${unsupported.join(', ')} have no engine-script equivalent yet — skipped.`);
        }
    }

    // ── Mesh material effects (keyframe driver) ───────────────────────────
    const meshTargets: MeshTarget[] = [];
    for (const [id, entity] of models) {
        const fx = meshFx.get(id);
        if (!fx) continue;
        const render = entity.findComponent('render') as pc.RenderComponent | null;
        const mi = render?.meshInstances?.[0];
        const material = mi?.material as pc.StandardMaterial | undefined;
        if (!material) continue;
        const span = lastFrame(
            fx.opacity?.values, fx.visible?.values, fx.diffuse?.color,
            fx.emissive?.intensity, fx.emissive?.color, fx.metalness?.values,
            fx.gloss?.values, fx.bumpiness?.values, fx.specularity?.values
        );
        meshTargets.push({ material, entity, fx, span });
    }

    let ticker: ((dt: number) => void) | null = null;
    if (meshTargets.length > 0) {
        const tmpColor = new pc.Color();
        let elapsed = 0;
        const maxSpan = Math.max(...meshTargets.map(t => t.span));
        const maxSec = maxSpan / FPS;
        const applyAt = (frame: number) => {
            for (const t of meshTargets) {
                const m = t.material;
                const fx = t.fx;
                let dirty = false;
                if (fx.opacity) {
                    m.opacity = sampleScalar(fx.opacity.values, frame, 1);
                    m.blendType = m.opacity < 0.999 ? pc.BLEND_NORMAL : pc.BLEND_NONE;
                    m.depthWrite = m.opacity >= 0.999;
                    dirty = true;
                }
                if (fx.metalness) { m.metalness = sampleScalar(fx.metalness.values, frame, m.metalness); dirty = true; }
                if (fx.gloss) { m.gloss = sampleScalar(fx.gloss.values, frame, m.gloss); dirty = true; }
                if (fx.bumpiness) { m.bumpiness = sampleScalar(fx.bumpiness.values, frame, m.bumpiness); dirty = true; }
                if (fx.specularity) { (m as unknown as { specularityFactor: number }).specularityFactor = sampleScalar(fx.specularity.values, frame, 1); dirty = true; }
                if (fx.diffuse && sampleColor(fx.diffuse.color, frame, tmpColor)) { m.diffuse.copy(tmpColor); dirty = true; }
                if (fx.emissive?.color && sampleColor(fx.emissive.color, frame, tmpColor)) { m.emissive.copy(tmpColor); dirty = true; }
                if (fx.emissive?.intensity) { m.emissiveIntensity = sampleScalar(fx.emissive.intensity, frame, m.emissiveIntensity); dirty = true; }
                if (fx.visible) { t.entity.enabled = sampleScalar(fx.visible.values, frame, 1) >= 0.5; }
                if (dirty) m.update();
            }
        };
        // Apply frame 0 immediately so the scene opens in the authored start state.
        applyAt(0);
        ticker = (dt: number) => {
            if (elapsed > maxSec) return;     // played through — hold final
            elapsed += dt;
            applyAt(Math.min(elapsed, maxSec) * FPS);
        };
        app.on('update', ticker);
    }

    return () => {
        if (ticker) app.off('update', ticker);
    };
}

/** Pull the per-element effects maps out of a parsed scene file. */
export function collectEffects(
    file: { splats?: { id: string; effects?: SplatEffects }[]; models?: { id: string; effects?: MeshEffects }[] }
): { splatFx: Map<string, SplatEffects>; meshFx: Map<string, MeshEffects> } {
    const splatFx = new Map<string, SplatEffects>();
    const meshFx = new Map<string, MeshEffects>();
    for (const s of file.splats ?? []) if (s.effects) splatFx.set(s.id, s.effects);
    for (const m of file.models ?? []) if (m.effects) meshFx.set(m.id, m.effects);
    return { splatFx, meshFx };
}
